import { useMemo, useCallback, useEffect, useRef } from 'react';
import React, { useState } from 'react';
import { DataGridPro, GridRow, gridFilteredSortedRowIdsSelector, useGridApiContext, GridColumnMenu } from '@mui/x-data-grid-pro';
import { useLocation } from 'react-router-dom';
import { useUser, getUserNameById } from '../UserContext';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import TimeAdjDialog from './TimeAdjustments/TimeAdjDialog'
import { useTimeAdjContext } from './TimeAdjustments/TimeAdjContext';
import { gridFilterModelSelector } from '@mui/x-data-grid-pro';
import SharedGridToolbar from './SharedGridToolbar';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { usePersistedDataStore } from '../store';
import { getTrainingUniquePids, isSelectionEqualToTraining as isSelectionEqualToTrainingUtil } from './common/trainingUtils';
import { updateOffersBatch } from './SummaryCaseTable/services/realtimeOfferService.js';
import { createOfferContext } from '../../utils/offerContextUtils';
import { buildColumnsWithHeaderStats } from './utils/dataGridColumnHeaderStats';
import { withSelectAllAwareCheckbox } from './utils/gridSelectAllCheckbox';
import logger from '../../utils/logger';
import { normalizeCompId, selectionKeyForRow } from './common/compSheetLookup';


function CustomUserItem(props) {
  const { myCustomHandler, myCustomValue } = props;

  return (
    <MenuItem onClick={myCustomHandler}>
      <ListItemIcon>
        <SettingsApplicationsIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{myCustomValue}</ListItemText>
    </MenuItem>
  );
}

// Create the custom time adjustment selection 
function CustomColumnMenu({ apiRef, setDialogOpen, setSelectedColumn, ...props }) {
  // logger.log('current column', currentColumn);
  const { openDialog } = useTimeAdjContext();
  
  const handleCustomAction = () => {
    // Use the TimeAdjContext to open the dialog
    openDialog(null);
    // Also call the original setDialogOpen for backward compatibility
    if (setDialogOpen) {
      setDialogOpen(true);
    }
  };

  return (
    <GridColumnMenu
    {...props}

      slots={{
        columnMenuUserItem: CustomUserItem,
        columnMenuColumnsItem: null,
        columnMenuPinningItem: null,
      }}
      slotProps={{
        columnMenuUserItem: {
          displayOrder: 15,
          myCustomValue: "Edit Time Adjustment",
          myCustomHandler: handleCustomAction,
        },
      }}
    />
  );
}

// This component re-renders if you click anything in datagrid toolbar. bad performance I think.
// Ultimately this Component ended up being redundant but is fine to seperate these things out a bit mroe from the monolithic MUI table component.
function DataGridContainer({
        addCompCallback,
        discardChanges,
        caseNotesChanged,
        userReordered,
        setIsSavingCallback,
        handleSetPropertyInfo,
        setIsPinned,
        reOrderingRef,
        view,
        userUpdate,
        handleProcessRowUpdate,
        mergedProps = { rows: [] },
        dataGridStyles,
        pinnedRows,
        apiRef,
        memoizedColumnVisibilityModel,
        handleColumnVisibilityChange,
        columnLayout,
        getRowClassName,
        getCellClassName,
        handleProcessRowUpdateError,
        filteredHeaders,
        isDifferent,
        saveChanges,
        subject,
        unsavedChangesRef,
        updateRefWithSubj,
        handleSaveComps,
        handleToggleReordering,
        reordering,
        headers,
        savedCompPids,
        selectedRows,
        isPinned,
        updateSelectedComps,
        onFilteredRowsChange,
        filteredParcelIds,
        mapFilteredParcelIds,
        onClearMapFilter,
        onManualRefresh,
        isRefreshing,
        isSaving,
        hideToolbar = false,
        isSettled = false,
        isSaleRun = false,
        }) {
          // define values which will be used for the PDF generator function.
          const location = useLocation();
          const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
          const comp = useMemo(() => parseInt(queryParams.get('comp')) - 1 || 0, [queryParams]);
          // use your user object here to pull in from user ID
          const { user } = useUser();
          const userName = user?.userName;
          const hiddenFields = ['actions',];

          // Get negotiation object to find the case data
          const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
          
          // Find the case object from negotiations using subject parcel_id
          const caseObject = useMemo(() => {
            if (!getNegotiationObj?.cases || !subject?.parcel_id) return null;
            return getNegotiationObj.cases.find(c => c.parcel_id === subject.parcel_id) || null;
          }, [getNegotiationObj?.cases, subject?.parcel_id]);

          // Build map of parcel_id -> array of { userId, index } for tooltip
          const trainingEntriesByParcelId = useMemo(() => {
            try {
              if (!caseObject?.TrainingComps || !Array.isArray(caseObject.TrainingComps)) return {};
              const result = {};
              caseObject.TrainingComps.forEach((entry) => {
                const userId = entry?.UserID;
                const compsDict = entry?.Comps || {};
                if (!userId || !compsDict) return;
                Object.entries(compsDict).forEach(([idx, pid]) => {
                  if (!pid) return;
                  if (!result[pid]) result[pid] = [];
                  result[pid].push({ userId, index: idx });
                });
              });
              return result;
            } catch (e) {
              return {};
            }
          }, [caseObject]);

          // Unique list of training PIDs for equality comparison
          const trainingUniquePids = useMemo(() => getTrainingUniquePids(caseObject), [caseObject]);

          const isSelectionEqualToTraining = useMemo(() => {
            const currentSelection = Array.isArray(selectedRows) ? selectedRows : (savedCompPids ?? []);
            return isSelectionEqualToTrainingUtil(currentSelection, trainingUniquePids);
          }, [selectedRows, savedCompPids, trainingUniquePids]);

          // Compose training tooltip content (same pattern as error tooltip) 
          const getTrainingTooltipContent = (params) => {
            // If user's selection equals training comps, suppress tooltip
            if (isSelectionEqualToTraining) return null;
            const pid = params?.row?.parcel_id;
            const entries = pid ? trainingEntriesByParcelId[pid] : undefined;
            if (!entries || entries.length === 0) return null;

            // Accept functionality updates saved comps AND training offers for the selected user
            const handleAccept = async (e) => {
              e?.stopPropagation?.();
              try {
                if (!caseObject?.TrainingComps || !Array.isArray(caseObject.TrainingComps)) return;
                
                // Determine which user's comps are being accepted (from the entries shown in tooltip)
                // If multiple users, use the first one (entries are for the specific parcel_id)
                const selectedUserId = entries && entries.length > 0 ? entries[0].userId : null;
                
                // Collect all training comp pids for the selected user, ordered by index ascending
                const pidIndexPairs = [];
                caseObject.TrainingComps.forEach((entry) => {
                  // Filter by selected user if we have one
                  if (selectedUserId && entry?.UserID !== selectedUserId) return;
                  
                  const comps = entry?.Comps || {};
                  Object.entries(comps).forEach(([idx, compPid]) => {
                    const n = Number(idx);
                    if (!compPid || Number.isNaN(n)) return;
                    pidIndexPairs.push([n, compPid]);
                  });
                });
                pidIndexPairs.sort((a, b) => a[0] - b[0]);
                const orderedUniquePids = Array.from(new Set(pidIndexPairs.map((p) => p[1])));
                
                // Collect training offers BEFORE saving comps (to avoid stale state)
                const trainingOffers = Array.isArray(caseObject?.TrainingOffers) ? caseObject.TrainingOffers : [];
                const offersToUpdate = [];
                
                if (trainingOffers.length > 0 && selectedUserId) {
                  // Filter offers by the same user that the comps are from
                  const userTrainingOffers = trainingOffers.filter(offer => 
                    offer?.Negotiator === selectedUserId
                  );
                  
                  if (userTrainingOffers.length > 0) {
                    // Collect ALL training offers for this user
                    userTrainingOffers.forEach(offer => {
                      offersToUpdate.push({
                        parcelId: caseObject?.parcel_id,
                        offerValue: offer.Offer, // AV value from training offer
                        offeredBy: offer.OfferedBy || 'Aventine',
                        offerDate: offer.OfferDate || new Date().toISOString(),
                        comments: offer.Comments || '',
                        response: offer.Response || null
                      });
                    });
                  }
                }
                
                // Save comps FIRST and wait for it to complete
                if (Array.isArray(orderedUniquePids) && orderedUniquePids.length > 0) {
                  await handleSaveComps?.(orderedUniquePids);
                }
                
                // Update offers AFTER comps save completes, using the latest negotiation object
                if (offersToUpdate.length > 0) {
                  logger.log('🔄 [TRAINING OFFERS] Updating offers for individual case:', {
                    parcelId: caseObject?.parcel_id,
                    userId: selectedUserId,
                    offerCount: offersToUpdate.length
                  });
                  
                  try {
                    // Get the LATEST negotiation object after comps save and delay
                    // This ensures we have the most up-to-date state including any updates from handleSaveComps
                    const latestNegotiationObj = usePersistedDataStore.getState().negotiationObj;
                    logger.log('🔍 [TRAINING OFFERS] Latest negotiation object before offer update:', {
                      parcelId: caseObject?.parcel_id,
                      hasNegotiations: Array.isArray(latestNegotiationObj.cases.find(c => c.parcel_id === caseObject?.parcel_id)?.Negotiations),
                      negotiationsLength: latestNegotiationObj.cases.find(c => c.parcel_id === caseObject?.parcel_id)?.Negotiations?.length || 0
                    });
                    
                    const context = createOfferContext(user, latestNegotiationObj, 'Manual');
                    
                    const offersResult = await updateOffersBatch(offersToUpdate, context, {
                      liveOffer: 0 // Training offers are not live
                    });
                    logger.log('✅ [TRAINING OFFERS] Offers updated successfully for individual case:', offersResult);
                    
                    // Verify Negotiations were preserved after update
                    const finalCheck = usePersistedDataStore.getState().negotiationObj;
                    const finalCase = finalCheck.cases.find(c => c.parcel_id === caseObject?.parcel_id);
                    logger.log('🔍 [TRAINING OFFERS] Final negotiation object after offer update:', {
                      parcelId: caseObject?.parcel_id,
                      hasNegotiations: Array.isArray(finalCase?.Negotiations),
                      negotiationsLength: finalCase?.Negotiations?.length || 0
                    });
                  } catch (offerError) {
                    logger.error('❌ [TRAINING OFFERS] Failed to update offers for individual case:', offerError);
                    // Don't fail the whole operation if offer update fails
                  }
                }
                
                // Note: We don't remove TrainingComps/TrainingOffers from the negotiation object
                // The existing isSelectionEqualToTraining logic will suppress the visual indicators
                // (borders and tooltips) when savedCompPids matches the training comps
              } catch (_e) {
                // no-opFor 
              }
            };
            return (
              <div className="error-tooltip">
                {entries.map((e, i) => (
                  <div key={`${pid}-${e.userId}-${e.index}-${i}`}>
                    Training comp #{e.index} saved by {getUserNameById?.(e.userId) || `User ${e.userId}`}
                  </div>
                ))}
                <div style={{ marginTop: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAccept}
                    sx={{
                      backgroundColor: '#4CAF50',
                      color: '#fff',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      minHeight: '24px',
                      '&:hover': {
                        backgroundColor: '#45a049',
                      },
                    }}
                  >
                    Accept
                  </Button>
                </div>
              </div>
            );
          };

          const [dialogOpen, setDialogOpen] = useState(false);
          const [selectedColumn, setSelectedColumn] = useState(null);
          const lastAppliedMapFilterRef = useRef([]);
          const isFirstRef = useRef(true);
          const lastOtherFiltersRef = useRef([]);
          const lastFilterWasFromMapRef = useRef(false);
          const lastQuickFilterRef = useRef([]);

          const rows = mergedProps?.rows || [];
          const columnsWithHeaderStats = useMemo(
            () => buildColumnsWithHeaderStats(mergedProps?.columns, mergedProps?.rows),
            [mergedProps?.columns, mergedProps?.rows],
          );

          const memoizedFilterModel = useMemo(() => {
            if (!filteredParcelIds) return undefined;
            return {
              items: [
                {
                  field: 'parcel_id',
                  operator: 'isAnyOf',
                  value: filteredParcelIds,
                },
              ],
            };
          }, [filteredParcelIds]);

          const ColumnMenuSlot = useCallback(
            (props) => (
              <CustomColumnMenu {...props} setDialogOpen={setDialogOpen} setSelectedColumn={setSelectedColumn} />
            ),
            []
          );

          const dataGridKey = React.useMemo(() => {
            // Include pinned rows in the total length calculation
            const totalLength = rows.length + (pinnedRows?.top?.length || 0);
            const first = rows[0]?.id;
            const last = rows[rows.length - 1]?.id;
            return `view-${view}-comp-${comp}-len-${totalLength}-ids-${first}-${last}`;
          }, [view, comp, rows.length, rows[0]?.id, rows[rows.length - 1]?.id, pinnedRows?.top?.length]);

          // Add effect to reset isFirstRef when comp changes
          useEffect(() => {
            isFirstRef.current = true;
          }, [comp]);

          useEffect(() => {
            if (!apiRef.current) return;

            const publish = () => {
              if (!apiRef.current) return;
              
              const rawIds = gridFilteredSortedRowIdsSelector(apiRef);
              let parcels = rawIds
                .map(id => apiRef.current.getRow(id)?.parcel_id)
                .filter(Boolean);

              // Pinned rows are omitted from gridFilteredSortedRowIdsSelector, so
              // reordered/selected comps would disappear from the map without this.
              const parcelForIdentity = (id) => {
                const needle = normalizeCompId(id);
                const fromPinned = (pinnedRows?.top || []).find((r) =>
                  selectionKeyForRow(r, isSaleRun) === needle || normalizeCompId(r.parcel_id) === needle
                );
                if (fromPinned?.parcel_id) return fromPinned.parcel_id;
                const allIds = apiRef.current.getAllRowIds?.() || [];
                for (const rid of allIds) {
                  const row = apiRef.current.getRow(rid);
                  if (row && (selectionKeyForRow(row, isSaleRun) === needle || normalizeCompId(row.parcel_id) === needle)) {
                    return row.parcel_id;
                  }
                }
                return isSaleRun ? null : id;
              };
              const savedParcels = (savedCompPids || []).map(parcelForIdentity).filter(Boolean);
              const selectedParcels = (selectedRows || []).map(parcelForIdentity).filter(Boolean);
              const pinnedParcels = (pinnedRows?.top || []).map((r) => r.parcel_id).filter(Boolean);
              parcels = [...new Set([...parcels, ...selectedParcels, ...pinnedParcels])];
              if (isPinned && savedParcels.length > 0) {
                parcels = [...new Set([...parcels, ...savedParcels])];
              }

              requestAnimationFrame(() => {
                onFilteredRowsChange(parcels);
              });
            };

            if (!apiRef.current) return;
            
            const unsubscribe = apiRef.current.subscribeEvent('filterModelChange', () => {
              setTimeout(() => {
                if (isFirstRef.current) {
                  isFirstRef.current = false;
                  return;
                }

                if (!apiRef.current) return;
                const currentModel = gridFilterModelSelector(apiRef);
                const parcelFilter = currentModel.items?.find(item => item.field === 'parcel_id');
                const currentOtherFilters = (currentModel.items?.filter(item => item.field !== 'parcel_id') || []);
                const hasOtherItemFilters = currentOtherFilters.length > 0;

                const currentQuickFilter = currentModel.quickFilterValues || [];
                const lastQuickFilter = lastQuickFilterRef.current;

                // Check if quick filter changed
                const didQuickFilterChange =
                  currentQuickFilter.length !== lastQuickFilter.length ||
                  currentQuickFilter.some((val, idx) => val !== lastQuickFilter[idx]);

                lastQuickFilterRef.current = currentQuickFilter;

                const hasQuickFilter = currentQuickFilter.length > 0;
                const hasOtherFilters = hasOtherItemFilters || hasQuickFilter;

                const isMapFilter =
                  parcelFilter &&
                  Array.isArray(parcelFilter.value) &&
                  parcelFilter.value.length === mapFilteredParcelIds?.length &&
                  parcelFilter.value.every(id => mapFilteredParcelIds.includes(id));

                // Detect whether other filters have changed
                const lastOtherFilters = lastOtherFiltersRef.current;
                const didOtherFiltersChange =
                  currentOtherFilters.length !== lastOtherFilters.length ||
                  currentOtherFilters.some((item, idx) =>
                    item.field !== lastOtherFilters[idx]?.field ||
                    item.operator !== lastOtherFilters[idx]?.operator ||
                    JSON.stringify(item.value) !== JSON.stringify(lastOtherFilters[idx]?.value)
                  ) || didQuickFilterChange;
                lastOtherFiltersRef.current = currentOtherFilters;

                if (isMapFilter && !hasOtherFilters && !didOtherFiltersChange) {
                  lastFilterWasFromMapRef.current = true;
                  return;
                }

                lastFilterWasFromMapRef.current = false;
                publish();
              }, 0);
            });

            // --- (2) Conditionally apply map filter ---
            if (Array.isArray(mapFilteredParcelIds)) {
              const last = lastAppliedMapFilterRef.current;
              const isSameAsLast = (
                last.length === mapFilteredParcelIds.length &&
                last.every((id, idx) => id === mapFilteredParcelIds[idx])
              );

              if (!isSameAsLast && apiRef.current) {
                const currentModel = gridFilterModelSelector(apiRef);
                const withoutOldParcelFilter = currentModel.items?.filter(item => item.field !== 'parcel_id') || [];

                const newModel = mapFilteredParcelIds.length
                  ? {
                      items: [
                        ...withoutOldParcelFilter,
                        {
                          field: 'parcel_id',
                          operator: 'isAnyOf',
                          value: mapFilteredParcelIds,
                        }
                      ],
                    }
                  : { items: withoutOldParcelFilter };

                if (apiRef.current) {
                  apiRef.current.setFilterModel(newModel);
                  lastAppliedMapFilterRef.current = [...mapFilteredParcelIds];
                }
              }
            }

            publish();

            return () => unsubscribe();
          }, [
            apiRef,
            onFilteredRowsChange,
            mapFilteredParcelIds,
            dataGridKey,
            isPinned,
            savedCompPids,
            selectedRows,
            pinnedRows,
            onClearMapFilter,
            isSaleRun,
          ]);

          const handleDialogSave = (updatedValue) => {
            setDialogOpen(false);
          };

          const getTogglableColumns = useCallback((columns) => {
            return headers
              .filter((column) => !hiddenFields.includes(column.field))
              .map((column) => column.field);
          }, [headers]);

          const loaderOverlay = useMemo(() => {
            if (!isRefreshing) return null;
            const toolbarHeight = 46;
            return (
              <>
                {/* Overlay on grid body only so toolbar stays visible and usable */}
                <div className="absolute z-[9998] inset-0 bg-gray-400 opacity-60 pointer-events-none grid-overlay" style={{ top: toolbarHeight }} />
                <div className="absolute z-[9999] mt-[70px] inset-0 flex items-center justify-center pointer-events-none" style={{ top: toolbarHeight }}>
                  <CircularProgress className="opacity-100" sx={{ color: '#374151' }} size={50}/>
                </div>
              </>
            );
          }, [isRefreshing]);

          // Helper function to create consolidated error messages
          const createConsolidatedErrorMessage = (errorMessages) => {
            // Group errors by type for better consolidation
            const errorGroups = {
              filters: [],
              adjustments: [],
              data: [],
              other: []
            };

            errorMessages.forEach(error => {
              const code = error.code || error;
              const message = error.message || error;
              
              // Categorize errors
              if (code.includes('Filter') || code.includes('Acreage') || code.includes('SquareFeet')) {
                errorGroups.filters.push(message);
              } else if (code.includes('Adj') || code.includes('adjustment')) {
                errorGroups.adjustments.push(message);
              } else if (code.includes('Sale') || code.includes('MLS') || code.includes('IFMV') || code.includes('Date')) {
                errorGroups.data.push(message);
              } else {
                errorGroups.other.push(message);
              }
            });

            // Build consolidated message
            const parts = [];
            
            // Add filter-related errors
            if (errorGroups.filters.length > 0) {
              if (errorGroups.filters.length === 1) {
                parts.push(errorGroups.filters[0]);
              } else {
                const filterTypes = errorGroups.filters.map(msg => {
                  if (msg.includes('acreage')) return 'acreage';
                  if (msg.includes('square footage')) return 'square footage';
                  if (msg.includes('gross adjustments')) return 'gross adjustments';
                  if (msg.includes('net adjustments')) return 'net adjustments';
                  return 'filters';
                });
                parts.push(`This comp has ${filterTypes.join(', ')} above the threshold`);
              }
            }
            
            // Add adjustment-related errors
            if (errorGroups.adjustments.length > 0) {
              if (errorGroups.adjustments.length === 1) {
                parts.push(errorGroups.adjustments[0]);
              } else {
                const adjTypes = errorGroups.adjustments.map(msg => {
                  if (msg.includes('gross adjustments')) return 'gross adjustments';
                  if (msg.includes('net adjustments')) return 'net adjustments';
                  return 'adjustments';
                });
                parts.push(`This comp has ${adjTypes.join(' and ')} above the threshold`);
              }
            }
            
            // Add data-related errors
            if (errorGroups.data.length > 0) {
              if (errorGroups.data.length === 1) {
                parts.push(errorGroups.data[0]);
              } else {
                const dataTypes = errorGroups.data.map(msg => {
                  if (msg.includes('non-arms-length')) return 'non-arms-length sale';
                  if (msg.includes('sale condition')) return 'sale condition issue';
                  if (msg.includes('bad IFMV')) return 'bad IFMV value';
                  if (msg.includes('outside the set date range')) return 'sale outside date range';
                  if (msg.includes('MLS number')) return 'missing MLS number';
                  return 'data issues';
                });
                parts.push(`This comp has ${dataTypes.join(', ')}`);
              }
            }
            
            // Add other errors
            if (errorGroups.other.length > 0) {
              parts.push(...errorGroups.other);
            }
            
            // Join all parts with appropriate connectors
            if (parts.length === 1) {
              return parts[0];
            } else if (parts.length === 2) {
              return `${parts[0]} and ${parts[1]}`;
            } else {
              const lastPart = parts.pop();
              return `${parts.join(', ')}, and ${lastPart}`;
            }
          };

          const getErrorTooltipContent = (params) => {
            const errors = params.row.errors;
            if (!errors || errors.length === 0) return null;
            
            // Get error messages
            const errorMessages = params.row.errorMessages || 
              errors.map(error => ({ code: error, message: error }));
            
            // If only one error, show it directly
            if (errorMessages.length === 1) {
              return (
                <div className="error-tooltip">
                  {errorMessages[0].message}
                </div>
              );
            }
            
            // For multiple errors, create a consolidated message
            const consolidatedMessage = createConsolidatedErrorMessage(errorMessages);
            
            return (
              <div className="error-tooltip">
                {consolidatedMessage}
              </div>
            );
          };

          const RowSlot = useCallback(
            (rowProps) => {
              const errorContent = getErrorTooltipContent(rowProps);
              const trainingContent = getTrainingTooltipContent(rowProps);
              const hasBoth = !!errorContent && !!trainingContent;
              return (
                <Tooltip
                  title={
                    errorContent || trainingContent ? (
                      <div>
                        {trainingContent}
                        {hasBoth && <div style={{ height: 6 }} />}
                        {errorContent}
                      </div>
                    ) : ''
                  }
                  arrow
                  disableInteractive={!trainingContent}
                  placement="bottom"
                >
                  <GridRow {...rowProps} />
                </Tooltip>
              );
            },
            [getErrorTooltipContent, getTrainingTooltipContent]
          );

          const memoizedSlots = useMemo(
            () => withSelectAllAwareCheckbox({
              columnMenu: ColumnMenuSlot,
              row: RowSlot,
              toolbar: SharedGridToolbar,
            }),
            [ColumnMenuSlot, RowSlot]
          );

        return(
            <div className="w-full h-full min-h-0 relative flex flex-col overflow-hidden">
              {(isRefreshing) && loaderOverlay}
            <TimeAdjDialog
              open={dialogOpen}
              onClose={() => setDialogOpen(false)}
              onSave={handleDialogSave}
            />
                <DataGridPro
                      key={dataGridKey}
                      {...mergedProps}
                      columns={columnsWithHeaderStats}
                      disableEqualOverflow
                      className="data-grid-transition"
                      sx={{
                        ...dataGridStyles,
                      }}
                    pinnedRows={pinnedRows}
                    keepNonExistentRowsSelected
                    disableColumnFilter={false}
                    columnBufferPx={150}
                    rowBufferPx={200}
                    virtualScrollerOptions={{
                      overscan: 5,     // Number of rows to render outside viewport
                      scrollThreshold: 0.2  // When to load more rows (0.2 = 20% from edge)
                    }}
                    autoHeight={false}
                    apiRef={apiRef}
                    columnVisibilityModel={memoizedColumnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    getRowClassName={getRowClassName}
                    getCellClassName={getCellClassName}
                    processRowUpdate={(updatedRow, originalRow) => handleProcessRowUpdate(updatedRow, originalRow)}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    hideFooter={true}
                    slots={memoizedSlots}
                    showToolbar
                    slotProps={{
                        baseTooltip: {
                            arrow: true,
                            enterDelay: 350,
                            slotProps: {
                                tooltip: {
                                    sx: {
                                        maxWidth: 440,
                                        whiteSpace: 'pre-line',
                                        typography: 'body2',
                                        lineHeight: 1.35,
                                        py: 0.75,
                                    },
                                },
                            },
                        },
                        toolbar: {
                            headers,
                            filteredHeaders,
                            subject,
                            updateRefWithSubj,
                            handleSetPropertyInfo,
                            unsavedChangesRef,
                            columnVisibilityModel: memoizedColumnVisibilityModel,
                            columnLayout,
                            showSettlement: true,
                            caseObject: caseObject, // Pass actual case object with negotiations data
                            isSettled: isSettled,
                            setIsPinned,
                            caseNotesChanged,
                            selectedRows,
                            isSaleRun,
                            userName,
                            compNumber: comp,
                            isDifferent,
                            setIsSavingCallback,
                            handleSaveComps,
                            saveChanges,
                            userUpdate,
                            discardChanges,
                            handleToggleReordering,
                            reordering,
                            addCompCallback,
                            userReordered,
                            isPinned,
                            reOrderingRef,
                            savedCompPids,
                            isSaving,
                            hideToolbar,
                            onManualRefresh,
                        },
                        columnsManagement: {
                            getTogglableColumns,
                        },
                    }}
                    disableVirtualization={false}
                    filterModel={memoizedFilterModel}
                />
            </div>
        )
    }

if (process.env.NODE_ENV === 'development') {
  DataGridContainer.whyDidYouRender = true;
}

export default React.memo(DataGridContainer);
